<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    label: string
    confirmLabel?: string
    confirmDuration?: number
    mode?: 'icon-only' | 'expand' | 'full'
  }>(),
  {
    mode: 'expand',
    confirmDuration: 6_000,
  },
)

const appliedConfirmLabel = computed(() => props.confirmLabel ?? props.label)

const emit = defineEmits<{
  (e: 'confirm'): void
}>()

const confirmMode = ref(false)
const delayedConfirm = refDebounced(confirmMode, 0)
function onConfirm() {
  if (confirmMode.value) {
    emit('confirm')
  } else {
    confirmMode.value = true

    useTimeoutFn(() => {
      confirmMode.value = false
    }, props.confirmDuration)
  }
}
</script>

<template>
  <Button
    theme="default-dense"
    data-confirmable-button-component
    class="group relative"
    :class="{
      'text-blue-700 hover:bg-blue-500/5': confirmMode,
    }"
    :style="{ '--confirm-duration': `${confirmDuration}ms` }"
    @click="onConfirm"
    :aria-label="mode === 'icon-only' ? (confirmMode ? appliedConfirmLabel : label) : undefined"
  >
    <template #icon-before>
      <slot name="icon" v-bind="{ confirmMode }">
        <IconChevronRight />
      </slot>
    </template>
    <span
      class="pointer-events-none absolute inset-0 z-10 rounded bg-blue-400/20 mix-blend-darken group-hover:bg-blue-500/5"
      :class="{
        'opacity-0': !confirmMode,
        'origin-left transition-[right] duration-[var(--confirm-duration)] ease-linear':
          confirmMode,
        'right-full': delayedConfirm,
      }"
    />
    <template v-if="mode !== 'icon-only'">
      <SlideHorizontal :duration="200"
        ><span v-if="!confirmMode" class="sr-only">{{ label }}</span></SlideHorizontal
      >
      <SlideHorizontal :duration="200">
        <span v-if="confirmMode" class="block whitespace-nowrap pl-2 text-s font-semibold"
          ><span class="sr-only" v-if="appliedConfirmLabel === label">Bestätigen: </span
          >{{ appliedConfirmLabel }}</span
        >
      </SlideHorizontal>
    </template>
  </Button>
</template>

<style scoped lang="scss">
[data-confirmable-button-component] :deep([data-icon-before]) {
  padding: 0;
}
</style>
